'use client'
import * as React from 'react'
import Image from 'next/image'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutesParameterized } from '@repo/web-utils/app-routes'

import { useSocialCuratedFeedQuery } from '@web/gen/feed'
import type { Post } from '@web/gen/types'
import { useFormatTimeSpan } from '@web/app/[locale]/(web)/masterfeed/_hooks/use-format-time-span'
import { VoteProjectButton } from './vote-project-button'
import { CommentButton } from '../action-buttons/comment-button'
import { UserAvatarLink } from '../discussion/user-avatar-link'

export const TrendingProjectsClient = ({ projects, slug }: { projects: Post[]; slug: string }) => {
  const [shouldFetch] = React.useState(false)
  const formatTimeSpan = useFormatTimeSpan()

  // Only enable the query when shouldFetch is true
  const { data } = useSocialCuratedFeedQuery(
    {
      input: {
        itemsPerPage: 4,
      },
    },
    {
      select: item => item.socialCuratedFeed.posts,
      enabled: shouldFetch,
    },
  )

  // Use server projects initially, then fetched data if available
  const displayProjects = shouldFetch && data ? data : projects

  const timeElapsed = (createTime: string) => {
    if (!createTime) {
      return ''
    }
    return formatTimeSpan(Date.now() - new Date(createTime).getTime())
  }

  return (
    <>
      <div className="mt-3 grid w-full h-full sm:grid-cols-2 gap-2">
        {displayProjects
          ? displayProjects.map(project => {
              return (
                <Link
                  key={`trending-project-${project.id}`}
                  href={AppRoutesParameterized.CLUBHOUSE_PROJECT(slug, project.postSlug || '')}
                  className="transition-shadow overflow-hidden duration-300 cursor-pointer p-4 pt-1 flex justify-between flex-col gap-3 border-[#1313151F] w-full border rounded-[12px] hover:border-[#0a0a0b38] hover:shadow-[0px_1px_3px_1px_#00000026,0px_1px_2px_0px_#0000004D] active:border-[#0A0A0B1A] active:shadow-none focus-visible:outline-[#2D52BE] active:bg-cf-grey-25"
                  data-testid="clubhouse-image-card"
                >
                  <div>
                    <div className="flex justify-between items-center pb-1">
                      <p className="text-12 text-cf-grey-600 py-3">
                        {timeElapsed(project.createTime)}
                      </p>
                    </div>
                    <div className="aspect-[3/2] w-full overflow-hidden rounded-t-[4px] mb-3">
                      <Image
                        src={
                          project?.assets?.[0]?.optimizedUrl ||
                          project?.assets?.[0]?.url ||
                          `https://www.creativefabrica.com/wp-content/uploads/2023/08/25/CF-Classes-Update-Templates-2-580x387.png`
                        }
                        width={250}
                        height={250}
                        alt={project.content}
                        className="bg-cf-white h-full w-full overflow-hidden object-cover rounded aspect-[3/2]"
                      />
                    </div>
                    <p className="text-14 text-cf-grey-800 line-clamp-5 text-ellipsis leading-5 break-all">
                      {project.content}
                    </p>
                  </div>
                  <div>
                    <div className="pb-3">
                      <UserAvatarLink user={project.user} />
                    </div>
                    <div className="flex gap-3 border-t pt-3 border-[#13131514]">
                      <VoteProjectButton upvotes={project.likesCount} postId={project.id} />
                      <CommentButton commentCount={project.commentsCount} />
                    </div>
                  </div>
                </Link>
              )
            })
          : null}
      </div>
    </>
  )
}
